import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import heroImage from "../../assets/images/faq-hero.jpg";
import { useDispatch } from 'react-redux';
import { createContactUsMessage } from '../../redux/slices/contactUsSlice';
import {
  IconMail,
  IconPhoneCall,
  IconClock,
  IconHeadset,
  IconHelpCircle,
  IconShare2,
  IconBrandFacebook,
  IconBrandInstagram,
  IconBrandTwitter,
} from '@tabler/icons-react';
import { FaCloudUploadAlt, FaPaperPlane, FaUser, FaTimes } from "react-icons/fa";

export default function ContactUs() {
  const dispatch = useDispatch();
  const { user, token } = useSelector((state) => state.auth);
  const isLoggedIn = !!token;

  // State for form fields
  const [formData, setFormData] = useState({
    fullName: isLoggedIn ? user?.full_name || '' : '',
    email: isLoggedIn ? user?.email || '' : '',
    reason: 'Delivery Issue',
    orderNumber: '',
    message: '',
  });

  // State for profile picture
  const [profilePicture, setProfilePicture] = useState(
    isLoggedIn && user?.profile_picture ? user.profile_picture : null
  );
  const [profilePictureFile, setProfilePictureFile] = useState(null);

  // State for uploaded files
  const [uploadedFiles, setUploadedFiles] = useState([]);

  // State for form submission status
  const [submissionStatus, setSubmissionStatus] = useState({
    show: false,
    type: '', // 'success' or 'error'
    message: ''
  });

  const handleInputChange = (e) => {
    const { id, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [id]: value
    }));
  };

  const handleProfilePictureChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setProfilePicture(reader.result);
        setProfilePictureFile(file);
      };
      reader.readAsDataURL(file);
    }
    e.target.value = ''; // Reset input to allow selecting the same file again
  };

  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files);
    const newFiles = files.map(file => ({
      name: file.name,
      size: file.size,
      file,
    }));
    setUploadedFiles(prev => [...prev, ...newFiles]);
    e.target.value = ''; // Reset input to allow selecting the same files again
  };

  const removeFile = (index) => {
    setUploadedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Create FormData object
    const formDataToSend = new FormData();

    // Add regular form fields
    formDataToSend.append('full_name', formData.fullName);
    formDataToSend.append('email', formData.email);
    formDataToSend.append('reason', formData.reason);
    formDataToSend.append('order_number', formData.orderNumber);
    formDataToSend.append('message', formData.message);

    // Handle profile picture
    if (profilePictureFile) {
      formDataToSend.append('profile_picture', profilePictureFile);
    } else if (profilePicture && typeof profilePicture === 'string') {
      try {
        const response = await fetch(profilePicture);
        const blob = await response.blob();
        const file = new File([blob], 'profile-picture.jpg', { type: blob.type });
        formDataToSend.append('profilePicture', file);
      } catch (error) {
        console.error('Error converting profile picture URL to file:', error);
      }
    }

    // Add uploaded files
    if (uploadedFiles.length > 0) {
      uploadedFiles.forEach((fileObj, index) => {
        formDataToSend.append(`files[${index}]`, fileObj.file);
      });
    }
    try {
      const result = await dispatch(createContactUsMessage(formDataToSend));

      // Check if the submission was successful
      if (result?.payload?.id) {
        // Show success message
        setSubmissionStatus({
          show: true,
          type: 'success',
          message: 'Message sent successfully! Our team will get back to you soon.'
        });

        // Reset form after successful submission
        setFormData({
          fullName: isLoggedIn ? user?.full_name || '' : '',
          email: isLoggedIn ? user?.email || '' : '',
          reason: 'Delivery Issue',
          orderNumber: '',
          message: '',
        });
        setUploadedFiles([]);
        setProfilePictureFile(null);
        if (!isLoggedIn) {
          setProfilePicture(null);
        }
      } else {
        // Show error message
        setSubmissionStatus({
          show: true,
          type: 'error',
          message: result?.payload?.message || 'Failed to send the message. Please try again.'
        });
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmissionStatus({
        show: true,
        type: 'error',
        message: 'An unexpected error occurred. Please try again later.'
      });
    }

    // Hide message after 5 seconds
    setTimeout(() => {
      setSubmissionStatus(prev => ({ ...prev, show: false }));
    }, 3000);
  };

  return (
    <div>
      {/* Hero Section */}
      <div className="relative w-full h-[70vh] bg-cover bg-center" style={{ backgroundImage: `url(${heroImage})` }}>
        <div className="relative z-10 max-w-screen-xl mx-auto px-4 h-full flex flex-col justify-center items-start">
          <h1 className="text-white font-bold text-3xl md:text-4xl mb-2">
            We're Here to Help!
          </h1>
          <p className="text-white text-base md:text-lg mb-6 max-w-[600px]">
            Need assistance? Get in touch with our support team for any inquiries related to orders, designs, or account management.
          </p>
          <button className="bg-[#FFD100] text-black font-semibold py-2 px-6 rounded hover:bg-[#FFC700] transition-colors">
            Get Help Now
          </button>
        </div>

        <div className="absolute bottom-0 left-0 w-full">
          <div className="bg-[#0B0C52] mx-auto w-full px-4 pt-6 text-sm text-white">
            <nav className="align-center">
              <Link to="/" className="hover:underline">Home</Link>
              <span className="mx-2">/</span>
              <span className="font-medium text-[#FFD700]">Contact Us</span>
            </nav>
          </div>
        </div>
      </div>

      {/* Ways to Reach Us */}
      <div className="bg-[#EEE8E8] py-10 px-4">
        <div className="max-w-screen-xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-[#1A237E] mb-8">
            Ways to Reach Us
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Customer Support */}
            <div className="bg-[#EEE8E8] border border-[#1A237E] rounded-md p-6 shadow-md text-left">
              <div className="flex items-center gap-2 mb-3 text-[#1A237E]">
                <IconHeadset size={24} />
                <h3 className="text-lg font-bold">Customer Support</h3>
              </div>
              <div className="flex items-center gap-2 mb-2 text-[#1F0B29]">
                <IconMail size={16} />
                <p>support@company.com</p>
              </div>
              <div className="flex items-center gap-2 mb-2 text-[#1F0B29]">
                <IconPhoneCall size={16} />
                <p>+1-800-555-1234</p>
              </div>
              <div className="flex items-center gap-2 text-[#1F0B29]">
                <IconClock size={16} />
                <p>Monday - Friday | 9 AM - 6 PM EST</p>
              </div>
            </div>

            {/* FAQ & Help Center */}
            <div className="bg-[#EEE8E8] border border-[#1A237E] rounded-md p-6 shadow-md text-left">
              <div className="flex items-center gap-2 mb-3 text-[#1A237E]">
                <IconHelpCircle size={24} />
                <h3 className="text-lg font-bold">FAQ &amp; Help Center</h3>
              </div>
              <p className="text-[#1F0B29] mb-4">
                Find answers to common questions in our{" "}
                <a
                  href="/help-center"
                  className="underline text-[#364add] hover:text-[#11083B]"
                >
                  Help Center
                </a>.
              </p>
              <button className="inline-flex items-center gap-2 bg-[#1A237E] text-white px-4 py-2 rounded transition-colors w-full justify-center">
                <IconHelpCircle size={16} />
                <span>Visit FAQ</span>
              </button>
            </div>

            {/* Social Media Support */}
            <div className="bg-[#EEE8E8] border border-[#1A237E] rounded-md p-6 shadow-md text-left">
              <div className="flex items-center gap-2 mb-3 text-[#1A237E]">
                <IconShare2 size={24} />
                <h3 className="text-lg font-bold">Social Media Support</h3>
              </div>
              <p className="text-[#1F0B29] mb-4">
                Reach out on our official social media channels
              </p>
              <div className="flex justify-center items-center gap-4 text-[#1F0B29]">
                <a
                  href="https://facebook.com"
                  aria-label="Facebook"
                  className="hover:text-[#4267B2]"
                >
                  <IconBrandFacebook size={24} />
                </a>
                <a
                  href="https://twitter.com"
                  aria-label="Twitter"
                  className="hover:text-[#1DA1F2]"
                >
                  <IconBrandTwitter size={24} />
                </a>
                <a
                  href="https://instagram.com"
                  aria-label="Instagram"
                  className="hover:text-pink-600"
                >
                  <IconBrandInstagram size={24} />
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Send Us a Message Section */}
      <div className="bg-white py-10 px-4">
        <div className="max-w-screen-md mx-auto">
          <h2 className="text-3xl font-bold text-[#1A237E] mb-6 text-left">
            Send Us a Message
          </h2>

          {/* Submission Status Message */}
          {submissionStatus.show && (
            <div className={`mb-6 p-4 rounded-md ${submissionStatus.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
              {submissionStatus.message}
            </div>
          )}

          {/* Profile Picture Section */}
          <div className="flex flex-col items-center mb-8">
            <div className="relative w-24 h-24 rounded-full overflow-hidden border-2 border-[#1A237E] mb-2">
              {profilePicture ? (
                <img
                  src={profilePicture}
                  alt="Profile"
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                  <FaUser size={32} className="text-gray-500" />
                </div>
              )}
            </div>
            <label
              htmlFor="profileUpload"
              className="bg-[#1A237E] text-white px-4 py-2 rounded-md text-center
                       cursor-pointer hover:bg-[#11083B] transition-colors
                       inline-flex items-center gap-2"
            >
              <FaCloudUploadAlt size={16} />
              <span>{profilePicture ? "Change Profile Picture" : "Upload Profile Picture"}</span>
            </label>
            <input
              type="file"
              id="profileUpload"
              className="hidden"
              accept="image/*"
              onChange={handleProfilePictureChange}
            />
            {isLoggedIn && <p className="text-[#1A237E] font-medium mt-2">{user?.full_name}</p>}
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Full Name */}
            <div className="flex flex-col">
              <label htmlFor="fullName" className="text-[#1A237E] font-semibold mb-1">
                Full Name
              </label>
              <input
                type="text"
                id="fullName"
                placeholder="Enter Your Full Name"
                value={formData.fullName}
                onChange={handleInputChange}
                className="border-2 border-[#1A237E] rounded px-3 py-2 text-[#1A237E]
                         placeholder-gray-400 focus:outline-none focus:border-[#1A237E]"
                required
              />
            </div>

            {/* Email */}
            <div className="flex flex-col">
              <label htmlFor="email" className="text-[#1A237E] font-semibold mb-1">
                Email
              </label>
              <input
                type="email"
                id="email"
                placeholder="Enter Your Email"
                value={formData.email}
                onChange={handleInputChange}
                className="border-2 border-[#1A237E] rounded px-3 py-2 text-[#1A237E]
                         placeholder-gray-400 focus:outline-none focus:border-[#1A237E]"
                required
              />
            </div>

            {/* Reason For Inquiry */}
            <div className="flex flex-col">
              <label htmlFor="reason" className="text-[#1A237E] font-semibold mb-1">
                Reason For Inquiry
              </label>
              <select
                id="reason"
                value={formData.reason}
                onChange={handleInputChange}
                className="border-2 border-[#1A237E] rounded px-3 py-2 text-[#1A237E]
                         focus:outline-none focus:border-[#1A237E]"
              >
                <option value="Delivery Issue">Delivery Issue</option>
                <option value="Order Status">Order Status</option>
                <option value="Product Inquiry">Product Inquiry</option>
                <option value="Feedback">Feedback</option>
                <option value="Other">Other</option>
              </select>
            </div>

            {/* Order Number (Optional) */}
            <div className="flex flex-col">
              <label htmlFor="orderNumber" className="text-[#1A237E] font-semibold mb-1">
                Order Number (Optional)
              </label>
              <input
                type="text"
                id="orderNumber"
                placeholder="Enter Your Order Number"
                value={formData.orderNumber}
                onChange={handleInputChange}
                className="border-2 border-[#1A237E] rounded px-3 py-2 text-[#1A237E]
                         placeholder-gray-400 focus:outline-none focus:border-[#1A237E]"
              />
            </div>

            {/* Message */}
            <div className="flex flex-col md:col-span-2">
              <label htmlFor="message" className="text-[#1A237E] font-semibold mb-1">
                Message
              </label>
              <textarea
                id="message"
                rows="4"
                placeholder="Enter Your Message"
                value={formData.message}
                onChange={handleInputChange}
                className="border-2 border-[#1A237E] rounded px-3 py-2 text-[#1A237E]
                         placeholder-gray-400 focus:outline-none focus:border-[#1A237E]"
                required
              />
            </div>

            {/* File Upload (Optional) */}
            <div className="flex flex-col md:col-span-2">
              <label htmlFor="fileUpload" className="text-[#1A237E] font-semibold mb-1">
                File Upload (Optional)
              </label>

              {/* File upload button */}
              <label
                htmlFor="fileUpload"
                className="bg-[#1A237E] text-white px-4 py-2 rounded-md text-center
                         cursor-pointer hover:bg-[#11083B] transition-colors
                         inline-flex items-center gap-2 w-fit mb-4"
              >
                <FaCloudUploadAlt size={16} />
                Upload Files
              </label>
              <input
                type="file"
                id="fileUpload"
                className="hidden"
                onChange={handleFileUpload}
                multiple
              />

              {/* Uploaded files list */}
              {uploadedFiles.length > 0 && (
                <div className="border border-gray-300 rounded p-4">
                  <h4 className="text-[#1A237E] font-medium mb-2">Uploaded Files:</h4>
                  <ul className="space-y-2">
                    {uploadedFiles.map((file, index) => (
                      <li key={index} className="flex justify-between items-center">
                        <span className="truncate max-w-xs">{file.name}</span>
                        <button
                          type="button"
                          onClick={() => removeFile(index)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <FaTimes />
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            {/* Send Button */}
            <div className="md:col-span-2">
              <button
                type="submit"
                className="w-full bg-[#FFD100] text-black font-semibold px-6 py-3 rounded
                         hover:bg-[#FFC700] transition-colors flex items-center justify-center"
              >
                Send
                <FaPaperPlane size={16} className="ml-2" />
              </button>
            </div>
          </form>

          <p className="text-black mt-6 text-center">
            Thank you for reaching out! Our team will get back to you within 24–48 hours.
          </p>
        </div>
      </div>
    </div>
  );
}